import { Header } from "components/DesignSystem/Header/Header";
import { DashboardLayout } from "components/DashboardLayout";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useGetRecycleBinContentQuery } from "store/apis/documents";
import { usePageTitle } from "hooks/usePageTitle";
import { RecycleBinListView } from "./RecycleBinListView";
import { RecycleBinGridView } from "./RecycleBinGridView";
import { NoDocumentItem } from "../NoDocumentItem";
import { useState } from "react";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "components/DesignSystem/ToggleGroup/ToggleGroup";
import ConditionalToolTip from "components/design/conditionalToolTip";
import Gridview from "static/images/GridView.svg";
import Listview from "static/images/ListView.svg";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Search } from "components/DesignSystem/Search/Search";
import { Button } from "components/DesignSystem/Button/Button";
import { Link } from "react-router-dom";

type RecycleBinProps = {
  appUrl?: string;
};

export const RecycleBin = ({ appUrl = "" }: RecycleBinProps) => {
  usePageTitle("Recycle Bin");
  const { uuid: groupId, documents_group_id } = useCurrentGroupContext();
  const [listView, setListView] = useState(true);

  const { update } = useUpdateQuery();
  const query = useQuery();
  const searchTerm = query.get("search");

  const { data: recycleBinItems, isLoading } = useGetRecycleBinContentQuery(
    { groupId, searchTerm: searchTerm || undefined },
    { skip: !groupId }
  );

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    update({ query: "search", value: value || null });
  };

  const documentsPath = `${appUrl}/documents`;

  const changeViewType = () => {
    setListView((prev) => !prev);
  };

  return (
    <DashboardLayout
      header={
        <div className="t-flex t-flex-col t-gap-3 t-pt-1">
          <Header
            v2
            title="Recycle Bin"
            breadcrumbs={[
              {
                name: "Documents",
                link: `${documentsPath}/${documents_group_id}`,
              },
            ]}
            bottom={
              <div className="t-flex t-items-center t-gap-4 t-justify-between t-w-full t-py-3">
                <div className="t-w-1/2">
                  <Search
                    placeholder="Search Documents"
                    onChange={handleSearch}
                    value={searchTerm || ""}
                    block
                  />
                </div>
                <div className="t-flex t-gap-3 t-items-center">
                  <Link to={`${documentsPath}/${documents_group_id}`}>
                    <Button size="small">Go to home</Button>
                  </Link>
                  <ToggleGroup
                    value={listView ? "list" : "grid"}
                    onValueChange={changeViewType}
                    size="small"
                  >
                    <ConditionalToolTip condition="Grid view">
                      <ToggleGroupItem value="grid">
                        <img src={Gridview} alt="Grid view" />
                      </ToggleGroupItem>
                    </ConditionalToolTip>
                    <ConditionalToolTip condition="List view">
                      <ToggleGroupItem value="list">
                        <img src={Listview} alt="List view" />
                      </ToggleGroupItem>
                    </ConditionalToolTip>
                  </ToggleGroup>
                </div>
              </div>
            }
          />
        </div>
      }
    >
      <Async.Root
        isLoading={isLoading}
        isSuccess={!isLoading}
        isEmpty={!recycleBinItems?.length}
      >
        <Async.Empty>
          <NoDocumentItem hideUploadText />
        </Async.Empty>
        <Async.Success>
          {listView ? (
            <RecycleBinListView
              items={recycleBinItems || []}
              groupId={groupId}
            />
          ) : (
            <RecycleBinGridView
              items={recycleBinItems || []}
              groupId={groupId}
            />
          )}
        </Async.Success>
      </Async.Root>
    </DashboardLayout>
  );
};
